import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { pagePath } from '@pages/pagePaths';
import { parseId, PipelineSelection } from './remoteJobRouteParams';

const REMOTE_JOB_ID_PARAM = 'remoteJobId';
const RUN_PARAM = 'run';
const STEP_PARAM = 'step';
const RUN_VERSION_PARAM = 'runVersion';

// The App run that a dataset page was opened from, and the version that run wrote
export interface PipelineOrigin extends PipelineSelection {
    producedVersionId?: string;
}

interface DataHubRouteParams {
    datasetId: string | null;
    versionId: string | null;
    origin: PipelineOrigin | null;
}

// History state of a dataset page that a pipeline step opened, so the page can step back to that step
export const OPENED_FROM_PIPELINE = { openedFromPipeline: true };

export function isOpenedFromPipeline(state: unknown): boolean {
    return typeof state === 'object' && state !== null && 'openedFromPipeline' in state;
}

export function readPipelineOrigin(params: URLSearchParams): PipelineOrigin | null {
    const remoteJobId = parseId(params.get(REMOTE_JOB_ID_PARAM));
    if (remoteJobId === null) return null;

    return {
        remoteJobId,
        runId: parseId(params.get(RUN_PARAM)) ?? undefined,
        step: params.get(STEP_PARAM) || undefined,
        producedVersionId: params.get(RUN_VERSION_PARAM) || undefined,
    };
}

function pipelineOriginQuery({ remoteJobId, runId, step, producedVersionId }: PipelineOrigin): string {
    const query = new URLSearchParams({ [REMOTE_JOB_ID_PARAM]: String(remoteJobId) });
    if (runId !== undefined) query.set(RUN_PARAM, String(runId));
    if (step) query.set(STEP_PARAM, step);
    if (producedVersionId) query.set(RUN_VERSION_PARAM, producedVersionId);
    return query.toString();
}

export function useDataHubRouteParams(): DataHubRouteParams {
    const { datasetId, versionId } = useParams();
    const [searchParams] = useSearchParams();

    return useMemo<DataHubRouteParams>(
        () => ({
            datasetId: datasetId ?? null,
            versionId: versionId ?? null,
            origin: readPipelineOrigin(searchParams),
        }),
        [datasetId, versionId, searchParams]
    );
}

// The origin stays in the query, so a reload or a copied link still names the run that wrote the version
export function datasetPath(
    datasetId: string,
    { versionId, origin }: { versionId?: string; origin?: PipelineOrigin } = {}
): string {
    const datasetPagePath = `${pagePath.DataHub}/datasets/${encodeURIComponent(datasetId)}`;
    const path = versionId ? `${datasetPagePath}/versions/${encodeURIComponent(versionId)}` : datasetPagePath;
    return origin ? `${path}?${pipelineOriginQuery(origin)}` : path;
}
